import React, { useMemo, useRef } from 'react';

import { AccountFieldDefinition } from '@flipdish/api-client-typescript';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import { type FieldProps, Field, useFormikContext } from 'formik';
import get from 'lodash/get';
import { Translate } from 'react-localize-redux';

import { formikValidate } from '../../../../../helpers/validation';
import { PortalFeatureFlags } from '../../../../../services/featureFlags/portalFeatureFlags';
import { useFeatureFlagValue } from '../../../../../services/featureFlags/useFeatureFlagValue';
import * as dynamicHelpers from '../bankAccFieldTextHelpers/DynamicLabel';
import FormItemLayout from '../FormItemLayout';
import BankAccountFormBussinesTypeFields from './BankAccountFormBussinesTypeFields';
import FieldWrapper from './FieldWrapper';
import { useBankAddressLookup } from './useBankAddressLookup';
import {
  type BankFormValues,
  type PopulatedAccountField,
  useBankCountryFields,
} from './useBankCountryFields';

export interface IPropsDynamicFields {
  countryFields: AccountFieldDefinition[] | undefined;
}
export type Props = IPropsDynamicFields;

// GB IBAN field injected into the form for GB; not returned by Zeus for GB by default.
const GB_IBAN_FIELD: AccountFieldDefinition = {
  Key: 1, // AccountFieldKey.Iban
  DisplayName: 'IBAN',
  MinLength: 22,
  MaxLength: 22,
};

const validate = (value: string, fieldName: string) => {
  return (
    formikValidate.required(value) || formikValidate.max_string_custom(100)(value.length, fieldName)
  );
};

const validateDynamic = (
  value: string,
  fieldName: string | undefined,
  maxLength: number | undefined,
  minLength: number | undefined
) => {
  return (
    formikValidate.required(value) ||
    (minLength &&
      fieldName &&
      formikValidate.min_string_custom(minLength)(value.length, fieldName)) ||
    (maxLength && fieldName && formikValidate.max_string_custom(maxLength)(value.length, fieldName))
  );
};

// Used for fields that are optional helpers (e.g. GB IBAN — auto-fills Sort Code +
// Account Number but is not itself required). Skips the required check; still
// validates length constraints if a value is entered.
const validateOptionalWithLength = (
  value: string,
  fieldName: string | undefined,
  maxLength: number | undefined,
  minLength: number | undefined
) => {
  if (!value) return undefined;
  return (
    (minLength &&
      fieldName &&
      formikValidate.min_string_custom(minLength)(value.length, fieldName)) ||
    (maxLength && fieldName && formikValidate.max_string_custom(maxLength)(value.length, fieldName))
  );
};

const DynamicFormFields = (props: Props) => {
  const { countryFields = [] } = props;
  const editView = window.location.pathname.endsWith('/edit');
  const { values } = useFormikContext<BankFormValues>();

  const gbIbanAutofillEnabled = useFeatureFlagValue(
    PortalFeatureFlags.bankAccount_gbIbanAutofill,
    false
  );
  // All CE-1263 UI/lookup behaviour is scoped to flag on + GB only.
  const isGbIbanFeature = gbIbanAutofillEnabled && values.BankCountryCode === 'GB';

  const [disabledFieldsBeforeAccountValidation, setDisabledFieldsBeforeAccountValidation] =
    React.useState({
      bankName: false,
      bankAddress: false,
      swift: false,
      sortCode: false,
      accountNumber: false,
      iban: false,
    });

  const [loadingFieldsDuringAccountValidation, setLoadingFieldsDuringAccountValidation] =
    React.useState({
      bankName: false,
      bankAddress: false,
      swift: false,
      sortCode: false,
      accountNumber: false,
      iban: false,
    });

  // Inject a virtual IBAN field for GB and enforce the correct field order.
  // Zeus returns Account Number (key 2) before Sort Code (key 12), so we
  // explicitly reorder: Sort Code → Account Number → IBAN (optional, at bottom).
  // Gated behind the bankAccount_gbIbanAutofill flag (org_id traffic type).
  const effectiveCountryFields = useMemo(() => {
    if (
      isGbIbanFeature &&
      countryFields.length > 0 &&
      !countryFields.some((f) => f.DisplayName === 'IBAN')
    ) {
      const sortCodeField = countryFields.find((f) => f.DisplayName === 'Sort Code');
      const accountNumberField = countryFields.find((f) => f.DisplayName === 'Account Number');
      if (sortCodeField && accountNumberField) {
        return [sortCodeField, accountNumberField, GB_IBAN_FIELD];
      }
      return [...countryFields, GB_IBAN_FIELD]; // fallback: append IBAN
    }
    return countryFields;
  }, [isGbIbanFeature, countryFields]);

  // Reference to store the debounce timer so we can clear it on blur
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const { triggerValidation, resetLookup, takeIbanDerivedEntryFields, lookupStatus, lookupPath } =
    useBankAddressLookup(
      setDisabledFieldsBeforeAccountValidation,
      setLoadingFieldsDuringAccountValidation,
      debounceTimerRef,
      gbIbanAutofillEnabled
    );

  useBankCountryFields({
    countryFields: effectiveCountryFields,
    isGbIbanFeature,
    onCountryChange: resetLookup,
  });

  const renderDynamicContent = () => {
    const dynamicFields = effectiveCountryFields.map(function (dynamicField, index) {
      return (
        <Grid item xs={12} key={index}>
          <FieldWrapper>
            <Field
              name={`PopulatedAccountFields.${index}.Value`}
              validate={(value: string) => {
                const isGbIbanField = isGbIbanFeature && dynamicField.DisplayName === 'IBAN';
                return isGbIbanField
                  ? validateOptionalWithLength(
                      value,
                      dynamicField.DisplayName,
                      dynamicField.MaxLength,
                      dynamicField.MinLength
                    )
                  : validateDynamic(
                      value,
                      dynamicField.DisplayName,
                      dynamicField.MaxLength,
                      dynamicField.MinLength
                    );
              }}
            >
              {({ field, form }: FieldProps) => {
                const { errors, isSubmitting, touched } = form;
                const fieldError = get(errors, field.name) as string | undefined;

                const isSwiftField = dynamicField.DisplayName === 'Swift';
                const isSortCodeField = dynamicField.DisplayName === 'Sort Code';
                const isAccountNumberField = dynamicField.DisplayName === 'Account Number';
                const isGbIbanField = isGbIbanFeature && dynamicField.DisplayName === 'IBAN';

                // Suppress transient Required errors while lookup is in flight
                // (GB IBAN feature only — master never suppressed these).
                const suppressWhileLoading =
                  isGbIbanFeature &&
                  (isSortCodeField || isAccountNumberField) &&
                  lookupStatus === 'loading';
                const showError = editView
                  ? !!fieldError && !suppressWhileLoading
                  : !!fieldError &&
                    !suppressWhileLoading &&
                    (get(touched, field.name) as boolean | undefined);

                const fieldDisabled = isGbIbanFeature
                  ? isSwiftField
                    ? isSubmitting || disabledFieldsBeforeAccountValidation.swift
                    : isSortCodeField
                      ? isSubmitting || disabledFieldsBeforeAccountValidation.sortCode
                      : isAccountNumberField
                        ? isSubmitting || disabledFieldsBeforeAccountValidation.accountNumber
                        : isGbIbanField
                          ? isSubmitting || disabledFieldsBeforeAccountValidation.iban
                          : isSubmitting
                  : isSwiftField
                    ? isSubmitting || disabledFieldsBeforeAccountValidation.swift
                    : isSubmitting;

                const showSpinner = isSwiftField && loadingFieldsDuringAccountValidation.swift;

                const sortCodeVal =
                  values.PopulatedAccountFields?.find(
                    (f: PopulatedAccountField) => f.DisplayName === 'Sort Code'
                  )?.Value || '';
                const accountNumberVal =
                  values.PopulatedAccountFields?.find(
                    (f: PopulatedAccountField) => f.DisplayName === 'Account Number'
                  )?.Value || '';
                const showSortCodeAccountLookupError =
                  isGbIbanFeature &&
                  isAccountNumberField &&
                  lookupStatus === 'failed' &&
                  lookupPath === 'sort_code' &&
                  sortCodeVal.length === 6 &&
                  accountNumberVal.length === 8;

                const ibanVal =
                  values.PopulatedAccountFields?.find(
                    (f: PopulatedAccountField) => f.DisplayName === 'IBAN'
                  )?.Value || '';
                const showIbanLookupError =
                  isGbIbanField &&
                  lookupStatus === 'failed' &&
                  lookupPath === 'iban' &&
                  ibanVal.length === 22;

                const shouldValidateOnBlur =
                  dynamicField.DisplayName === 'IBAN' ||
                  dynamicField.DisplayName === 'Sort Code' ||
                  dynamicField.DisplayName === 'Account Number';

                const isCompleteGbIban = (value: string) =>
                  isGbIbanField && (value?.length ?? 0) >= 22;

                /** Normalises GB IBAN to uppercase for consistent 22-char and country-prefix checks. */
                const normaliseIbanInput = (value: string) =>
                  isGbIbanField ? value.toUpperCase() : value;

                const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
                  field.onBlur(e);

                  if (shouldValidateOnBlur) {
                    if (isCompleteGbIban(field.value as string)) {
                      form.setFieldTouched('BankCountryCode', true, false);
                    }
                    if (debounceTimerRef.current) {
                      clearTimeout(debounceTimerRef.current);
                      debounceTimerRef.current = null;
                    }
                    triggerValidation();
                  }
                };

                return (
                  <TextField
                    variant="standard"
                    {...field}
                    onChange={(e) => {
                      const strippedValue = e.target.value.replace(/\s/g, '');
                      const newValue = normaliseIbanInput(strippedValue);
                      if (isGbIbanFeature && isGbIbanField && newValue) {
                        // Clear Sort Code + Account Number so Finance only receives IBAN.
                        // Also drop stale Bank Name/Address from a prior lookup.
                        form.setFieldValue(
                          'PopulatedAccountFields',
                          (values.PopulatedAccountFields || []).map(
                            (f: PopulatedAccountField, i: number) => {
                              if (i === index) {
                                return {
                                  Key: dynamicHelpers.getDynamicKey(dynamicField.Key),
                                  DisplayName: dynamicField.DisplayName,
                                  Value: newValue,
                                };
                              }
                              if (
                                f.DisplayName === 'Sort Code' ||
                                f.DisplayName === 'Account Number'
                              ) {
                                return { ...f, Value: '' };
                              }
                              return f;
                            }
                          ),
                          false
                        );
                        form.setFieldValue('BankName', '', false);
                        form.setFieldValue('BankAddress', '', false);
                        setDisabledFieldsBeforeAccountValidation((prev) => ({
                          ...prev,
                          bankName: true,
                          bankAddress: true,
                        }));
                      } else if (
                        isGbIbanFeature &&
                        (isSortCodeField || isAccountNumberField) &&
                        newValue
                      ) {
                        // Clear IBAN; clear sibling only when both were IBAN-derived.
                        // Stale Bank Name/Address from the prior lookup must go too —
                        // otherwise a successful IBAN result stays visible after the
                        // user switches to a different Sort Code / Account Number.
                        const siblingDisplayName = isSortCodeField ? 'Account Number' : 'Sort Code';
                        const shouldClearSibling = takeIbanDerivedEntryFields();
                        form.setFieldValue(
                          'PopulatedAccountFields',
                          (values.PopulatedAccountFields || []).map(
                            (f: PopulatedAccountField, i: number) => {
                              if (i === index) {
                                return {
                                  Key: dynamicHelpers.getDynamicKey(dynamicField.Key),
                                  DisplayName: dynamicField.DisplayName,
                                  Value: newValue,
                                };
                              }
                              if (f.DisplayName === 'IBAN') {
                                return { ...f, Value: '' };
                              }
                              if (shouldClearSibling && f.DisplayName === siblingDisplayName) {
                                return { ...f, Value: '' };
                              }
                              return f;
                            }
                          ),
                          false
                        );
                        form.setFieldValue('BankName', '', false);
                        form.setFieldValue('BankAddress', '', false);
                        setDisabledFieldsBeforeAccountValidation((prev) => ({
                          ...prev,
                          bankName: true,
                          bankAddress: true,
                        }));
                      } else {
                        // Pre-CE-1263 / non-GB path: simple per-index write.
                        form.setFieldValue(`PopulatedAccountFields.${index}`, {
                          Key: dynamicHelpers.getDynamicKey(dynamicField.Key),
                          DisplayName: dynamicField.DisplayName,
                          Value: newValue,
                        });
                      }
                    }}
                    onBlur={handleBlur}
                    //@ts-ignore
                    label={
                      isGbIbanField ? (
                        <Translate id="Iban_recommended" />
                      ) : (
                        <Translate id={`${dynamicHelpers.getDynamicLabel(dynamicField.Key)}`} />
                      )
                    }
                    autoComplete="off"
                    fullWidth
                    disabled={fieldDisabled}
                    error={showError || showSortCodeAccountLookupError || showIbanLookupError}
                    inputProps={{
                      'data-fd': `bank_dynamic_fields_${dynamicHelpers.getDynamicLabel(
                        dynamicField.Key
                      )}`,
                      ...(isSwiftField
                        ? {
                            'aria-busy': loadingFieldsDuringAccountValidation.swift,
                          }
                        : {}),
                    }}
                    InputProps={
                      showSpinner
                        ? {
                            endAdornment: (
                              <InputAdornment position="end">
                                <CircularProgress size={20} />
                              </InputAdornment>
                            ),
                          }
                        : undefined
                    }
                    InputLabelProps={{
                      shrink: true,
                    }}
                    helperText={
                      showSortCodeAccountLookupError ? (
                        <Translate id="UK_Sort_code_account_number_finance_lookup_error" />
                      ) : showIbanLookupError ? (
                        <Translate id="UK_Iban_finance_lookup_error" />
                      ) : showError ? (
                        <>{fieldError}</>
                      ) : undefined
                    }
                    margin="none"
                  />
                );
              }}
            </Field>
          </FieldWrapper>
        </Grid>
      );
    });
    return dynamicFields;
  };

  return (
    <FormItemLayout label={<Translate id="Bank_account" />}>
      {/* Persistent lookup lifecycle for a11y + E2E; not visual UI. */}
      <div
        data-fd="bank-account-lookup"
        aria-busy={lookupStatus === 'loading'}
        data-status={lookupStatus}
        hidden
      />
      {!editView && <BankAccountFormBussinesTypeFields />}
      {editView && (
        <FieldWrapper>
          <Field name={'BusinessType'}>
            {({ field }: FieldProps) => {
              return (
                <TextField
                  variant="standard"
                  {...field}
                  label={<Translate id={'Account_type'} />}
                  fullWidth
                  inputProps={{
                    'data-fd': 'Account_type',
                    readOnly: true,
                    disableUnderline: true,
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  margin="none"
                />
              );
            }}
          </Field>
        </FieldWrapper>
      )}

      <FieldWrapper>
        <Field name={'AccountName'} validate={(value: string) => validate(value, 'Account Name')}>
          {({ field, form }: FieldProps) => {
            const { errors, touched, isSubmitting } = form;
            const fieldError = errors[field.name] as string | undefined;
            const showError = editView
              ? !!fieldError
              : !!fieldError && (get(touched, field.name) as boolean | undefined);

            return (
              <TextField
                variant="standard"
                {...field}
                label={<Translate id={'Account_name'} />}
                autoComplete="off"
                fullWidth
                disabled={isSubmitting}
                error={showError}
                inputProps={{
                  'data-fd': 'Bank_account_name',
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                helperText={showError ? fieldError : undefined}
                margin="none"
              />
            );
          }}
        </Field>
      </FieldWrapper>
      {renderDynamicContent()}

      <FieldWrapper>
        <Field
          name={'BankName'}
          validate={(value: string) =>
            // GB IBAN feature: skip Required while locked (API-owned).
            // Pre-CE-1263: always validate.
            isGbIbanFeature && disabledFieldsBeforeAccountValidation.bankName
              ? undefined
              : validate(value, 'Bank Name')
          }
        >
          {({ field, form }: FieldProps) => {
            const { errors, touched, isSubmitting } = form;
            const fieldError = get(errors, field.name) as string | undefined;
            const bankNameLocked =
              isGbIbanFeature && disabledFieldsBeforeAccountValidation.bankName;
            const showError = editView
              ? !!fieldError && !bankNameLocked
              : !!fieldError &&
                !bankNameLocked &&
                (get(touched, field.name) as boolean | undefined);

            return (
              <TextField
                variant="standard"
                {...field}
                label={<Translate id={'Bank_name'} />}
                autoComplete="off"
                fullWidth
                disabled={isSubmitting || disabledFieldsBeforeAccountValidation.bankName}
                error={showError}
                inputProps={{
                  'data-fd': 'Bank_name',
                  'aria-busy': loadingFieldsDuringAccountValidation.bankName,
                }}
                InputProps={
                  loadingFieldsDuringAccountValidation.bankName
                    ? {
                        endAdornment: (
                          <InputAdornment position="end">
                            <CircularProgress size={20} />
                          </InputAdornment>
                        ),
                      }
                    : undefined
                }
                InputLabelProps={{
                  shrink: true,
                }}
                helperText={showError ? <>{fieldError}</> : undefined}
                margin="none"
              />
            );
          }}
        </Field>
      </FieldWrapper>

      <FieldWrapper>
        <Field
          name={'BankAddress'}
          validate={(value: string) =>
            isGbIbanFeature && disabledFieldsBeforeAccountValidation.bankAddress
              ? undefined
              : validate(value, 'Address')
          }
        >
          {({ field, form }: FieldProps) => {
            const { errors, touched, isSubmitting } = form;
            const fieldError = errors[field.name] as string | undefined;
            const bankAddressLocked =
              isGbIbanFeature && disabledFieldsBeforeAccountValidation.bankAddress;
            const showError = editView
              ? !!fieldError && !bankAddressLocked
              : !!fieldError && !bankAddressLocked && (touched[field.name] as boolean | undefined);

            return (
              <TextField
                variant="standard"
                {...field}
                label={<Translate id="Bank_Address" />}
                autoComplete="off"
                fullWidth
                multiline
                maxRows={3}
                disabled={isSubmitting || disabledFieldsBeforeAccountValidation.bankAddress}
                error={showError}
                inputProps={{
                  'data-fd': 'Bank_address',
                  'aria-busy': loadingFieldsDuringAccountValidation.bankAddress,
                }}
                InputProps={
                  loadingFieldsDuringAccountValidation.bankAddress
                    ? {
                        endAdornment: (
                          <InputAdornment position="end">
                            <CircularProgress size={20} />
                          </InputAdornment>
                        ),
                      }
                    : undefined
                }
                InputLabelProps={{
                  shrink: true,
                }}
                helperText={showError ? fieldError : undefined}
                margin="none"
              />
            );
          }}
        </Field>
      </FieldWrapper>
    </FormItemLayout>
  );
};

export default DynamicFormFields;
